import { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Stack,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

// Increment/Decrement Field Component
const IncrementDecrementField = ({ label, value, onChange, min = 0, sqft = false }) => {
  return (
    <TextField
      value={value}
      onChange={(e) => onChange(Math.max(min, Number(e.target.value)))}
      variant="outlined"
      label={label}
      fullWidth
      type="number"
      InputLabelProps={{ shrink: true }}
      inputProps={{ min }}
      sx={{
        '& .MuiOutlinedInput-root': { height: 60, borderRadius: 2 },
        '& .MuiInputBase-input': { textAlign: "center", fontWeight: 500, fontSize: 16 },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <IconButton
              size="small"
              onClick={() =>
                onChange(sqft ? Math.max(min, value - 100) : Math.max(min, value - 1))
              }
              disabled={value <= min}
              sx={{
                borderRadius: "50%",
                border: "1px solid",
                borderColor: "grey.300",
                width: 32,
                height: 32,
                padding: 0,
                color: "text.secondary",
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => onChange(sqft ? value + 100 : value + 1)}
              sx={{
                borderRadius: "50%",
                border: "1px solid",
                borderColor: "grey.300",
                width: 32,
                height: 32,
                padding: 0,
                color: "text.secondary",
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

// Main Component
const EstimateCalculator = () => {
  const [bedrooms, setBedrooms] = useState(0);
  const [bathrooms, setBathrooms] = useState(0);
  const [sqft, setSqft] = useState(0);
  const [cleaningType, setCleaningType] = useState("Standard Cleaning");
  const [pets, setPets] = useState(false);
  const [houseCondition, setHouseCondition] = useState("Normal");
  const [extraTasks, setExtraTasks] = useState({
    oven: false,
    refrigerator: false,
    windows: false,
    basement: false,
    laundry: false,
    garage: false,
  });
  const [cookingFrequency, setCookingFrequency] = useState("Occasionally");
  const [cookingStyle, setCookingStyle] = useState("Moderate");
  const [estimate, setEstimate] = useState({ oneCleaner: 0, twoCleaners: 0 });

  useEffect(() => {
    if (cleaningType === "Move-Out/Move-In Cleaning") {
      setExtraTasks({
        oven: true,
        refrigerator: true,
        windows: true,
        basement: true,
        laundry: true,
        garage: true,
      });
    } else {
      setExtraTasks({
        oven: false,
        refrigerator: false,
        windows: false,
        basement: false,
        laundry: false,
        garage: false,
      });
    }
  }, [cleaningType]);

  const calculateEstimate = () => {
    let baseHours = 0;
    baseHours += bedrooms * 0.5;
    baseHours += bathrooms * 0.75;
    baseHours += (sqft / 500) * 1;

    if (extraTasks.basement && cleaningType !== "Move-Out/Move-In Cleaning") baseHours += 1;
    if (extraTasks.laundry && cleaningType !== "Move-Out/Move-In Cleaning") baseHours += 0.5;
    if (extraTasks.garage && cleaningType !== "Move-Out/Move-In Cleaning") baseHours += 0.75;

    if (cleaningType === "Deep Cleaning") baseHours *= 1.5;
    if (cleaningType === "Move-Out/Move-In Cleaning") baseHours *= 2;

    if (houseCondition === "Moderately Dirty") baseHours *= 1.25;
    if (houseCondition === "Heavily Soiled") baseHours *= 1.5;

    if (pets) baseHours += 0.5;
    if (cookingFrequency === "Frequently") baseHours += 1;
    if (cookingStyle === "Heavy") baseHours += 1;

    ["oven", "refrigerator", "windows"].forEach((task) => {
      if (extraTasks[task] && cleaningType !== "Move-Out/Move-In Cleaning") {
        if (task === "oven") baseHours += 1;
        if (task === "refrigerator") baseHours += 0.5;
        if (task === "windows") baseHours += 1;
      }
    });

    const roundToHalfHour = (time) => (time <= 4 ? Math.ceil(time * 2) / 2 : Math.ceil(time));
    setEstimate({
      oneCleaner: roundToHalfHour(baseHours),
      twoCleaners: roundToHalfHour(baseHours / 2),
    });
  };

  useEffect(() => {
    calculateEstimate();
  }, [
    bedrooms,
    bathrooms,
    sqft,
    cleaningType,
    pets,
    houseCondition,
    extraTasks,
    cookingFrequency,
    cookingStyle,
  ]);

  const fields = [
    { label: "Bedrooms", value: bedrooms, setter: setBedrooms },
    { label: "Bathrooms", value: bathrooms, setter: setBathrooms, step: 0.5 },
    { label: "Square Footage", value: sqft, setter: setSqft, sqft: true, step: 100 },
    { label: "Cleaning Type", value: cleaningType, setter: setCleaningType, options: ["Standard Cleaning", "Deep Cleaning", "Move-Out/Move-In Cleaning"] },
    { label: "Pets", value: pets ? "Yes" : "No", setter: (v) => setPets(v === "Yes"), options: ["No", "Yes"] },
    { label: "House Condition", value: houseCondition, setter: setHouseCondition, options: ["Normal", "Moderately Dirty", "Heavily Soiled"] },
    { label: "Cooking Frequency", value: cookingFrequency, setter: setCookingFrequency, options: ["Rarely", "Occasionally", "Frequently"] },
    { label: "Cooking Style", value: cookingStyle, setter: setCookingStyle, options: ["Light", "Moderate", "Heavy"] },
  ];

  return (
    <Box sx={{ mt: 10, mb: 12, px: { xs: 2, sm: 4 } }}>
      <Typography variant="h4" fontWeight="bold" color="#005F60" align="center" gutterBottom>
        MaidLink Cleaning Time Estimator
      </Typography>
      <Typography align="center" sx={{ mb: 6, color: "#555555", fontSize: 16 }}>
        Quickly estimate your cleaning time based on bedrooms, bathrooms, home size, condition, and extras.
      </Typography>

      <Paper
        elevation={10}
        sx={{
          p: { xs: 4, sm: 6 },
          maxWidth: 800,
          mx: "auto",
          borderRadius: 3,
          backgroundColor: "#f9f9f9",
        }}
      >
        <Grid container spacing={4}>
          {fields.map((field, index) => (
            <Grid key={index} item xs={12} sm={6}>
              {field.options ? (
                <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { height: 60 } }}>
  <InputLabel shrink htmlFor={`select-${field.label}`} sx={{ fontSize: 14 }}>
    {field.label}
  </InputLabel>
  <Select
    label={field.label}
    value={field.value}
    onChange={(e) => field.setter(e.target.value)}
    inputProps={{
      id: `select-${field.label}`,
    }}
    sx={{
      '& .MuiSelect-outlined': { padding: '16.5px 14px' }, // adjust top padding so label doesn’t overlap
    }}
  >
    {field.options.map((opt) => (
      <MenuItem key={opt} value={opt}>
        {opt}
      </MenuItem>
    ))}
  </Select>
</FormControl>

              ) : (
                <IncrementDecrementField
                  label={field.label}
                  value={field.value}
                  onChange={field.setter}
                  min={0}
                  sqft={field.sqft}
                />
              )}
            </Grid>
          ))}

          {cleaningType !== "Move-Out/Move-In Cleaning" && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Additional Tasks
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                {Object.keys(extraTasks).map((task) => (
                  <Chip
                    key={task}
                    label={task.charAt(0).toUpperCase() + task.slice(1)}
                    color={extraTasks[task] ? "primary" : "default"}
                    onClick={() =>
                      setExtraTasks((prev) => ({ ...prev, [task]: !prev[task] }))
                    }
                    sx={{
                      cursor: "pointer",
                      mb: 1,
                      fontWeight: 500,
                      "&:hover": { transform: "scale(1.08)", transition: "0.3s" },
                    }}
                  />
                ))}
              </Stack>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 6 }} />

        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom color="#005F60">
            Estimated Cleaning Time
          </Typography>
          <Typography sx={{ fontSize: 18, mb: 1 }}>
            <strong>One Cleaner:</strong> {estimate.oneCleaner} hours
          </Typography>
          <Typography sx={{ fontSize: 18 }}>
            <strong>Two Cleaners:</strong> {estimate.twoCleaners} hours
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default EstimateCalculator;
